// src/pages/dashboard/feature/WeatherCalendar.jsx
import React from "react";
import { Paper, Grid, Typography, Box, Collapse } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";

function WeatherCalendar({
    calendarOpen,
    selectedDate,
    onDateChange,
    paperShadow
}) {
    //if (!calendarOpen) return null;

    return (
        <Collapse 
            in={calendarOpen}
            timeout={300} // Tambahkan timeout untuk kontrol kecepatan
            easing={{enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)'}} // Kurva easing yang smooth
            sx={{
                width: '100%', // Pastikan lebar 100%
                overflow: 'hidden' // Tambahkan overflow hidden
            }}
        >
            <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 4 }}>
                    {/* Kalender di sebelah kiri menggunakan MUI X DateCalendar */}
                    <Paper
                        sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 2,
                            boxShadow: paperShadow,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            localeText={{
                                calendarWeekNumberHeaderText: "#",
                                calendarWeekNumberText: (weekNumber) => `${weekNumber}.`,
                                previousMonth: "Bulan sebelumnya",
                                nextMonth: "Bulan berikutnya",
                            }}
                        >
                            <DateCalendar
                                value={selectedDate}
                                onChange={onDateChange}
                                displayWeekNumber
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    maxHeight: "280px",
                                    minHeight: "280px",
                                    "& .MuiDateCalendar-root": {
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                    },
                                    "& .MuiPickersCalendarHeader-root": {
                                        marginTop: 0,
                                        marginBottom: 2,
                                        px: 0,
                                        width: "100%",
                                        maxWidth: "100%",
                                        flexShrink: 0,
                                        "& .MuiPickersCalendarHeader-labelContainer": {
                                            flex: 1,
                                            justifyContent: "center",
                                        },
                                    },
                                    "& .MuiDayCalendar-weekDayLabel": {
                                        fontWeight: "bold",
                                        color: "text.secondary",
                                        width: "calc(100% / 8)",
                                        margin: 0,
                                        px: 0.5,
                                        flexShrink: 0,
                                    },
                                    "& .MuiPickersDay-root": {
                                        width: 36,
                                        height: 36,
                                        borderRadius: "8px",
                                        "&.Mui-selected": {
                                            backgroundColor: "primary.main",
                                            "&:hover": {
                                                backgroundColor: "primary.dark",
                                            },
                                        },
                                        "&:hover": {
                                            backgroundColor: "action.hover",
                                            borderRadius: "50%",
                                        },
                                    },
                                    "& .MuiPickersWeekNumber-root": {
                                        fontWeight: "bold",
                                        color: "text.secondary",
                                        width: "calc(100% / 8)",
                                        margin: 0,
                                        px: 0.5,
                                    },
                                    "& .MuiDayCalendar-slideTransition": {
                                        flex: 1,
                                        height: "100%",
                                        minHeight: "200px",
                                        display: "flex",
                                        flexDirection: "column",
                                    },
                                    "& .css-jaf7w8-MuiDateCalendar-root .MuiDayCalendar-monthContainer": {
                                        height: "100% !important",
                                        width: "100%",
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                    },
                                    "& .MuiDayCalendar-header": {
                                        width: "100%",
                                        maxWidth: "100%",
                                        margin: 0,
                                        padding: 0,
                                        justifyContent: "space-between",
                                        flexShrink: 0,
                                    },
                                    "& .MuiDayCalendar-weekContainer": {
                                        width: "100%",
                                        maxWidth: "100%",
                                        margin: 0,
                                        justifyContent: "space-between",
                                        flex: 1,
                                        "&:last-child": {
                                            marginBottom: 0,
                                        },
                                    },
                                    "& .MuiDayCalendar-weekNumberLabel": {
                                        width: "calc(100% / 8)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        margin: 0,
                                        padding: 0,
                                    },
                                    "& .MuiYearCalendar-root": {
                                        width: "100%",
                                        maxWidth: "100%",
                                    },
                                    "& .MuiYearCalendar-yearButton": {
                                        width: "25%",
                                        minWidth: "auto",
                                        margin: "4px 0",
                                    },
                                    "& .MuiPickersYear-yearButton": {
                                        width: "100%",
                                        justifyContent: "center",
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 6, md: 8 }}>
                    {/* Area untuk fitur cuaca di sebelah kanan */}
                    <Paper
                        sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 2,
                            boxShadow: paperShadow,
                            height: "calc(100% - 24px)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Informasi Cuaca
                        </Typography>
                        <Typography variant="body2" color="text.disabled" align="center" sx={{ mb: 2 }}>
                            Fitur cuaca akan segera hadir
                        </Typography>
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: "50%",
                                backgroundColor: "action.hover",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                            }}
                        >
                            <Typography variant="h3" color="text.disabled">
                                ☀️
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.disabled" align="center">
                            Data cuaca real-time akan ditampilkan di sini
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Collapse>
    );
}

export default WeatherCalendar;